import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { StorageKeys } from '@core/constants';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import {
  IS_AUTH_ENABLED,
  IS_CAPTCHA_ENABLED,
} from '@core/services/base.service';
import { EnvironmentService } from '@core/services/environment.service';
import { StorageService } from '@core/services/storage/storage.service';
import { Logout } from '@core/state/auth/auth.actions';
import { getJsonBlobFromFormData } from '@core/utils/get-json-blob-from-form-data.util';
import { readJsonFromBlob } from '@core/utils/read-json-from-blob.util';
import { Store } from '@ngrx/store';
import { InsuranceErrorComponent } from '@shared/components/insurance-error/insurance-error.component';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  BehaviorSubject,
  catchError,
  filter,
  Observable,
  Observer,
  Subscription,
  switchMap,
  take,
  throwError,
} from 'rxjs';

import { OtpComponent } from '../../shared/components/otp/otp.component';
import { InsuranceInterceptorHandler } from './insurance-interceptor-handler.service';

@Injectable({
  providedIn: 'root',
})
export class MainInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(null);
  ref: DynamicDialogRef<any> | null | undefined;

  private readonly auth: AuthenticationService = inject(AuthenticationService);
  private readonly environmentService: EnvironmentService =
    inject(EnvironmentService);
  private readonly storageService: StorageService = inject(StorageService);
  private readonly store: Store = inject(Store);
  private readonly router: Router = inject(Router);
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly insuranceInterceptorHandler = inject(
    InsuranceInterceptorHandler
  );

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let authReq = req.clone();
    const user = this.auth.userValue || {};

    if (req.body instanceof FormData) {
      authReq = req.clone();
    } else {
      authReq = req.clone({
        setHeaders: { 'Content-Type': 'application/json' },
      });
    }

    if (
      user &&
      user.authToken &&
      req.context.get(IS_AUTH_ENABLED) &&
      !req.url.includes('/invitations/') &&
      !req.url.includes('/timezones') &&
      !req.url.includes('/impersonate/token/')
    ) {
      authReq = this.addToken(authReq, user.authToken);
    }

    if (user.impersonatorEmail && !this.isCmsRequest(req)) {
      authReq = authReq.clone({
        setHeaders: {
          'x-impersonator-email': user.impersonatorEmail,
        },
      });
    }

    if (
      req.context.get(IS_CAPTCHA_ENABLED) &&
      this.storageService.get(StorageKeys.captchaResponse)
    ) {
      authReq = authReq.clone({
        setHeaders: {
          'x-captcha-response': this.storageService.get(
            StorageKeys.captchaResponse
          ),
        },
      });
    }

    if (this.storageService.get(StorageKeys.csrfAuth)) {
      authReq = authReq.clone({
        setHeaders: {
          'X-CSRF-TOKEN': this.storageService.get(StorageKeys.csrfAuth)
            ?.csrfToken,
        },
      });
    }

    if (this.insuranceInterceptorHandler.shouldHandle(req)) {
      return this.insuranceInterceptorHandler.handleRequest(
        authReq,
        req,
        next,
        this.handleWithError.bind(this)
      );
    }

    return this.handleWithError(authReq, req, next);
  }

  private isCmsRequest(req: HttpRequest<any>): boolean {
    const cmsUrl = this.environmentService.cmsUrl.replace(/\/$/, '');

    return req.url === cmsUrl || req.url.startsWith(`${cmsUrl}/`);
  }

  private forwardObservable(
    source$: Observable<HttpEvent<any>>,
    observer: Observer<HttpEvent<any>>
  ): Subscription {
    return source$.subscribe({
      next: (event) => observer.next(event),
      error: (err) => observer.error(err),
      complete: () => observer.complete(),
    });
  }

  private handleWithError(
    authReq: HttpRequest<any>,
    originalReq: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(authReq).pipe(
      catchError((error) => {
        const wwwAuthenticate = error.headers?.get('WWW-authenticate');
        if (
          (error instanceof HttpErrorResponse &&
            error.status === 401 &&
            error.error?.errorCode === 'UNAUTHENTICATED_001') ||
          (wwwAuthenticate && wwwAuthenticate.includes('invalid_token'))
        ) {
          return this.handle401Error(originalReq, next);
        } else if (403 === error.status) {
          this.router.navigate(['/shipping/ship']);
        } else if (error.error?.errorCode === 'OTP_UNAUTHORIZED_001') {
          return this.handleOtpError(originalReq, next);
        } else if (
          error.error?.errorCode === 'DUPLICATE_PICKUP_000' &&
          !originalReq.url.includes('/pickups') // /shipping/pickups/one-time/{id} and /shipping/pickups/create-one-time  and /shipping/pickups/parcel-pickup
        ) {
          return this.handlePickupError(authReq, next, error);
        } else if (
          error instanceof HttpErrorResponse &&
          this.insuranceInterceptorHandler.isHandledError(
            error,
            originalReq.url
          )
        ) {
          return this.insuranceInterceptorHandler.handleError(
            authReq,
            next,
            error
          );
        } else if (
          error instanceof HttpErrorResponse &&
          error.status === 500 &&
          error.error?.errorCode === 'REST_API_CALL_001'
        ) {
          return this.handle500Error();
        }
        let errorMsg = '';
        if (error.error instanceof ErrorEvent) {
          errorMsg = `Error: ${error.error.message}`;
        } else {
          const errMsg = error?.error?.errorMessage;
          if (typeof errMsg === 'string') {
            errorMsg = errMsg;
          } else if (errMsg && typeof errMsg === 'object') {
            errorMsg = JSON.stringify(errMsg);
          } else {
            errorMsg = error?.message || 'An unexpected error occurred.';
          }
        }
        return throwError(() => errorMsg);
      })
    );
  }

  private handle500Error() {
    return throwError(() => 'An unexpected error occurred.');
  }

  private handle401Error(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.auth.refreshAccessToken().pipe(
        switchMap((newToken) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(newToken);
          return next
            .handle(this.addToken(req, newToken))
            .pipe(
              catchError((retryErr) =>
                throwError(() => this.extractErrorMessage(retryErr))
              )
            );
        }),
        catchError((err) => {
          this.isRefreshing = false;
          if (
            typeof err !== 'string' ||
            err === 'Your session has expired. Please login again.'
          ) {
            this.store.dispatch(Logout());
          }
          return throwError(() =>
            typeof err === 'string' ? err : this.extractErrorMessage(err)
          );
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) =>
          next
            .handle(this.addToken(req, token!))
            .pipe(
              catchError((retryErr) =>
                throwError(() => this.extractErrorMessage(retryErr))
              )
            )
        ),
        catchError((err) => {
          if (
            typeof err !== 'string' ||
            err === 'Your session has expired. Please login again.'
          ) {
            this.store.dispatch(Logout());
          }
          return throwError(() =>
            typeof err === 'string' ? err : this.extractErrorMessage(err)
          );
        })
      );
    }
  }

  private extractErrorMessage(error: any): string {
    if (error.error instanceof ErrorEvent) {
      return `Error: ${error.error.message}`;
    }
    const errMsg = error?.error?.errorMessage;
    if (typeof errMsg === 'string') return errMsg;
    if (errMsg && typeof errMsg === 'object') return JSON.stringify(errMsg);
    return error?.message || 'An unexpected error occurred.';
  }

  private handleOtpError(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return new Observable((observer) => {
      let email = '';

      if (req.body instanceof FormData) {
        if (req.body.get('requestDTO')) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const result = JSON.parse(reader.result as string);
              email =
                result.requester?.email ||
                result.requesterContact?.email ||
                email;
              this.openOtpDialog(email, req, next, observer);
            } catch (err) {
              console.error('Error parsing JSON from FormData Blob:', err);
              observer.error('Invalid FormData content');
            }
          };
          reader.readAsText(req.body.get('requestDTO') as Blob);
        }
      } else if (typeof req.body === 'object' && req.body !== null) {
        email =
          req.body.requester?.email ||
          req.body.requesterContact?.email ||
          email;
        this.openOtpDialog(email, req, next, observer);
      } else {
        observer.error('Invalid request body format');
      }
    });
  }

  private openOtpDialog(
    email: string,
    req: HttpRequest<any>,
    next: HttpHandler,
    observer: Observer<HttpEvent<any>>
  ) {
    this.storageService.delete(StorageKeys.csrfAuth);
    this.ref = this.dialogService.open(OtpComponent, {
      focusOnShow: true,
      modal: true,
      closable: false,
      closeOnEscape: false,
      data: { email },
      width: '400px',
    });

    // Wait for OTP verification and retrieve CSRF token
    this.ref?.onClose.subscribe((csrfResp) => {
      if (csrfResp) {
        this.storageService.save(csrfResp, StorageKeys.csrfAuth);

        // Clone request with CSRF token
        const clonedReq = req.clone({
          setHeaders: { 'X-CSRF-TOKEN': csrfResp.csrfToken },
        });

        // Resend the request with CSRF token and forward the response
        next.handle(clonedReq).subscribe({
          next: (res: any) => {
            if (res.ok) {
              this.storageService.delete(StorageKeys.csrfAuth);
            }
            observer.next(res);
          },
          error: (error) => {
            observer.error(error.error.errorMessage);
          },
          complete: () => observer.complete(),
        });
      } else {
        console.error('OTP Dialog closed without CSRF Token');
        observer.error('OTP verification failed');
      }
    });
  }

  private addToken(
    req: HttpRequest<any>,
    token: string | null
  ): HttpRequest<any> {
    return token
      ? req.clone({
          setHeaders: { Authorization: `Bearer ${token}` },
        })
      : req;
  }

  private handlePickupError(
    req: HttpRequest<any>,
    next: HttpHandler,
    error: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    return new Observable((observer) => {
      this.ref = this.dialogService.open(InsuranceErrorComponent, {
        modal: true,
        closable: false,
        closeOnEscape: false,
        width: '460px',
        data: {
          option: 'New Pickup',
          secondaryButtonText: 'Update Pickup Details',
          message:
            'A pickup already exists for this address, carrier, and time. Would you like to continue without creating a new pickup, or update the pickup details?',
          title: 'Pickup Already Exists!',
        },
      });

      this.ref?.onClose.subscribe((result) => {
        if (result === 'CONTINUE_WITHOUT_OPTION') {
          this.resendWithoutPickup(req, next, observer);
        } else {
          // RETURN_TO_SHIPMENT
          observer.complete();
        }
      });
    });
  }

  private resendWithoutPickup(
    req: HttpRequest<any>,
    next: HttpHandler,
    observer: Observer<HttpEvent<any>>
  ): void {
    if (req.body instanceof FormData) {
      const originalFormData = req.body;
      const jsonBlob = getJsonBlobFromFormData(originalFormData);

      if (jsonBlob) {
        readJsonFromBlob(
          jsonBlob,
          (requestDto) => {
            try {
              if (requestDto.pickupFromShipmentRequestDTO) {
                delete requestDto.pickupFromShipmentRequestDTO;
              }

              const updatedJson = JSON.stringify(requestDto);

              const updatedBlob = new Blob([updatedJson], {
                type: 'application/json',
              });

              const newFormData = new FormData();

              originalFormData.forEach((value, key) => {
                if (key === 'requestDTO') {
                  newFormData.append(key, updatedBlob, 'blob');
                } else {
                  newFormData.append(key, value as any);
                }
              });

              const modifiedReq = req.clone({ body: newFormData });

              this.forwardObservable(next.handle(modifiedReq), observer);
            } catch (e) {
              observer.error('Failed to modify request payload (pickup).');
            }
          },
          () => {
            observer.error('Failed to read request payload (pickup).');
          }
        );

        return;
      }
    }

    if (
      req.body &&
      typeof req.body === 'object' &&
      !(req.body instanceof FormData)
    ) {
      const newBody: any = { ...req.body };

      if (newBody.pickupFromShipmentRequestDTO) {
        delete newBody.pickupFromShipmentRequestDTO;
      }

      const modifiedReq = req.clone({ body: newBody });

      this.forwardObservable(next.handle(modifiedReq), observer);

      return;
    }

    observer.error('Unsupported request body format for pickup removal.');
  }
}

export const mainInterceptorFn: HttpInterceptorFn = (req, next) =>
  inject(MainInterceptor).intercept(req, {
    handle: (r: HttpRequest<any>) => next(r),
  } as HttpHandler);
